import {
  APP_GRAPHQL_DOCUMENTS,
  type AppGraphqlOperation,
  GUEST_DESTINATION_KINDS,
  GUEST_DESTINATION_PREFIX,
  type GuestRouteLocation,
  type GuestDestination,
  type GuestDestinationKind,
  type UdsSqlPage,
  type FrameSqlOptions,
  type NormalizedFrameSqlOptions,
  type PaginatedFrameQueryResult,
  type UdsRowLimitParameter,
} from '@doss/configbase-common/apps'

// postMessage protocol between the host page and the sandboxed guest iframe.
//
// The frame runs with an OPAQUE origin (iframe sandbox="allow-scripts allow-downloads allow-pointer-lock", no
// allow-same-origin) and a CSP of default-src 'none'; connect-src 'none', so
// postMessage is its ONLY host/network channel. Host cookies, storage, GraphQL,
// and any network egress (fetch/XHR/WebSocket) are unreachable from guest code.
//
// Host-side rule: trust event.source identity (=== iframe.contentWindow),
// never event.origin — an opaque origin serializes as the string 'null',
// which is shared by every sandboxed frame on the page.

/**
 * LEGACY_CJS_MIGRATE: ENG-601 — Replace legacy version attribution in consumers/telemetry before removal.
 * Handshake protocol version. Host and frame must agree before any RPC — a
 * stale cached frame (older bundle) is refused rather than allowed to
 * half-speak the protocol. Bump on any breaking change to the messages below.
 */
export const GUEST_FRAME_PROTOCOL_VERSION = 8

/**
 * Import specifiers a guest may use. The single source of truth for BOTH
 * enforcement layers: the host-side compile scan (compile.ts, friendly
 * errors) and the frame's require map (frame-main.tsx, the hard gate — it
 * fails the frame at boot if its module map ever drifts from this list).
 */
export const ALLOWED_GUEST_IMPORTS: ReadonlySet<string> = new Set([
  '@doss/app',
  'react',
  'react/jsx-runtime',
  'react/jsx-dev-runtime',
  'react-router',
  '@doss/guest-runtime',
  '@doss/arp',
  '@doss/arp/icons',
  'echarts',
  'ag-grid-community',
  'ag-grid-enterprise',
  'ag-grid-react',
])

/** A specifier that Doss itself publishes. */
export const isFirstPartyGuestImport = (specifier: string): boolean =>
  specifier.startsWith('@doss/')

/**
 * Host-enforced budgets for guest SQL. Enforcement lives host-side (the web
 * GuestFrame and the byofe render harness enforce identical limits) and the
 * values are part of the guest contract: a guest must design its queries
 * within them, and `doss byofe contract` reports them to authoring tools
 * alongside the import allowlist.
 */
export const GUEST_QUERY_BUDGETS = {
  /** Max characters in one serialized SQL statement. */
  maxSerializedQueryLength: 50_000,
  /** Max in-flight SQL, endpoint and destination-resolve requests per frame, one shared budget;
   * the rest queue FIFO. */
  maxConcurrentQueryRequests: 16,
  /**
   * Max UTF-8 byte length of the exact JSON-serialized rows array. Enforced only by hosts that
   * measure results themselves (the local render harness); the production web host relies on the
   * server's own 4 MiB result cap instead of re-checking the server-reported byte count.
   */
  maxResultBytes: 5_000_000,
} as const

/** Host-enforced endpoint call budgets; in flight, they share `maxConcurrentQueryRequests` with SQL. */
export const GUEST_ENDPOINT_BUDGETS = {
  /** Max UTF-8 bytes of one call's JSON input: the endpoints service's own cap. */
  maxInputBytes: 1024 * 1024,
  maxLogicalIdLength: 128,
} as const

/** Machine-readable endpoint-call surface in the App authoring contract. */
export const GUEST_ENDPOINT_AUTHORING_CONTRACT = {
  imports:
    "import { getOrder } from 'endpoints/orders' — by name. Internal endpoints, helpers and " +
    '`import * as` are refused (DOSS4008).',
  query:
    'useEndpointQuery(getOrder, { id }, { enabled, staleTime, refetchInterval }) → { data, error, ' +
    'isLoading, isFetching, refetch }. Cached per input; with no input, pass undefined before the ' +
    'options. refetchInterval is at least 1000 ms. Dates arrive as Dates.',
  mutation:
    'useEndpointMutation(createOrder, { invalidates: [listOrders] }) → { mutate, mutateAsync, ' +
    "data, error, isPending, reset }. On success every listed endpoint's queries refetch. " +
    'An endpoint declaring .idempotency({ key }) replays a repeat of the same key.',
  errors:
    "error is an EndpointError with the service's code and status. Queries retry a network " +
    'failure or 5xx up to three times; mutations never retry on their own. getOrder.call() throws ' +
    'in an app.',
  permissions:
    "The viewer calls as themselves; their role needs the endpoint, unless they're an admin.",
} as const

/** Machine-readable useSql surface printed by `doss byofe contract`. */
export const GUEST_SQL_AUTHORING_CONTRACT = {
  paginated: {
    page: {
      required: ['limit'],
      firstPage: {},
      explicitOffset: { offset: 0 },
      selection: 'keyset-preferred-with-offset-fallback',
      continuations: 'runtime-managed',
    },
    options: ['page', 'includeTotalCount'],
    metadata: ['hasNext', 'totalCount'],
    controls: ['nextPage', 'refetch'],
  },
} as const

/**
 * Host-enforced budgets for guest navigation. Like GUEST_QUERY_BUDGETS, the values are part of the guest contract and
 * `doss byofe contract` reports them to authoring tools.
 *
 * Sizing principle: rate budgets degrade gracefully, so legitimate usage is not affected.
 * Size caps exist only where the system would break, and target the system's limit rather than below it.
 */
export const GUEST_NAVIGATION_BUDGETS = {
  /** External signed URLs (S3, OAuth) legitimately run 1-4 KB; this only guards abuse. */
  maxHrefLength: 16_000,
  maxPathLength: 8_000,
  maxSearchLength: 8_000,
  /** Sliding-window push budget; over-budget pushes degrade to replace, never drop. */
  pushRate: { maxPushes: 20, windowMs: 10_000 },
  /** Max |delta| per guest-history-go traversal. */
  maxHistoryTraversalDelta: 100,
} as const

/** Machine-readable guest navigation surface printed by `doss byofe contract`. */
export const GUEST_NAVIGATION_AUTHORING_CONTRACT = {
  anchors: {
    external:
      'Absolute http(s) hrefs open in a NEW TAB (noopener); the app tab never navigates away.',
    doss:
      "Root-relative href + target='_top' (or '_parent') navigates the Doss app to that route " +
      'in place (SPA, no reload); a modified click opens it in a new tab.',
    protocolHandoff: 'mailto: and tel: hrefs launch the OS handler; the page stays put.',
    guestSpace:
      'Plain root-relative hrefs route inside the app via react-router. On the standalone ' +
      'embed page the app path and the entire search string mirror into the host URL ' +
      "(sharable links, bookmarks); a modified click or target='_blank' opens the app " +
      'route in a new tab.',
    fragments: "'#id' hrefs scroll to the element in place; no history entry is created.",
    denied:
      "javascript:, data:, custom app protocols, and protocol-relative '//' hrefs are " +
      'cancelled; named window targets have no meaning in the sandbox.',
    cancellation:
      'An onClick handler calling event.preventDefault() cancels any anchor action, exactly ' +
      'like a native anchor.',
    activation:
      'External, new-tab, and protocol-handler requests require a trusted user activation — ' +
      'programmatic element.click() cannot reach outside the app. In-app routing clicks act ' +
      'like stock react-router, trusted or not.',
  },
  windowApis:
    'window.open is sandbox-blocked. Navigating the frame document ' +
    '(location.href/assign/replace) destroys the running app — the host remounts it fresh. ' +
    'Author anchors instead.',
  router: {
    api:
      "The 'react-router' module is the real declarative API (Routes/Route/Link/NavLink/" +
      'Navigate/Outlet, the location/params/search hooks, path utilities). Router ' +
      'constructors and data-mode APIs (loaders, Form, ScrollRestoration) throw.',
    rules:
      'The host URL is the source of truth: standalone embed pages mirror guest routes into ' +
      'sharable URLs; grid embeds route in memory. state is not synced — use the path or ' +
      'search params. Paths are strict (no trailing slash, fragments, or dot segments; ' +
      'malformed paths are dropped whole). navigate(delta) traverses the real browser ' +
      'history and can leave the embed. Scroll: top on push, restored on Back/Forward, kept ' +
      'on replace — prefer setSearchParams(value, { replace: true }) for continuous ' +
      'controls; over-budget pushes degrade to replace.',
    declarative:
      "application('id').define([...routes]) declares the app as react-router route objects " +
      '(Component, element, index, path, children, ErrorBoundary; loaders, actions and lazy ' +
      'are refused) and mounts them with useRoutes. Every route has a literal id; ' +
      "App.routes.<id> is its ref. handle: { title: 'Orders' } titles the page — the deepest " +
      "matched title wins and the host appends it. search: (query) => ({ status: query.enum(['open', " +
      "'late']).default('open'), page: query.number().default(1), sku: query.string() }) declares " +
      'query keys (string, number, boolean, date, dateTime, enum; .default() on all but the ' +
      "dates); a descendant's ref declares its ancestors' keys too. A key with a default is " +
      'always present on read; any other may be undefined; a value outside its kind reads as ' +
      'the default and logs a diagnostic. Read with useRouteParams(ref) (path params + search ' +
      'values) or useRouteSearch(ref) → [values, setSearch] (setSearch writes the keys given, ' +
      'clears one given as undefined, and leaves the rest). Link with <DossLink to={ref} ' +
      'params={{ … }}>, href(ref, params) or useDossNavigate()(ref, params) — params are ' +
      'typed off the ref, path params required, and a link carries only the search keys it is ' +
      "given, never the current URL's. doss check reads the routes off the source, so ids, " +
      'paths, titles and search shapes must be literals ' +
      '(DOSS4003–4007). A component declared before the tree that returns a fragment or a bare ' +
      'value built from its refs needs an explicit return type (): ReactNode, or TypeScript ' +
      "reports TS7022. Link out of the app with the same APIs: import another app's module and " +
      'pass its routes (Other.routes.<id>) to DossLink, href or useDossNavigate — it bundles as ' +
      'those route refs only. A link out of the app is a reserved /~doss/… href the frame turns ' +
      "into a request to the host, which opens the target's own route (a new tab on a modified " +
      'click); hand it to any to or href, never build or parse one. Hovering the link swaps in ' +
      'the real host URL, so copy link address works; a target the viewer cannot see opens ' +
      'nothing and says so. An app route path may not start with ~doss (DOSS4009).',
  },
} as const

// LEGACY_CJS_REMOVE: ENG-601 — Retire this ready message and its guard with the CJS handshake.
/** Frame -> host: the frame runtime booted and is ready for guest-init. */
export type FrameReadyMessage = {
  type: 'guest-frame-ready'
  protocolVersion: number
  buildSha: string
}

/**
 * LEGACY_CJS_REMOVE: ENG-601 — Retire this init message and its guard after all CJS hosts migrate.
 * Host -> frame: render this compiled guest module (sucrase output, CJS).
 * `annotationsEnabled` mounts the in-jail Agentation feedback toolbar; the host sends true only
 * for internal (doss) users. Optional so older hosts/frames interop without a version bump.
 * `route` present = the mount owns a URL and the guest router runs synced; absent = memory mode.
 */
export type FrameInitMessage = {
  type: 'guest-init'
  protocolVersion: number
  code: string
  annotationsEnabled?: boolean
  route?: GuestRouteLocation
}

/** Frame -> host: run one raw UDS SQL statement for the guest. */
export type FrameSqlRequestMessage = {
  type: 'guest-sql'
  id: number
  /** Request-scoped cache bypass used by the hook's explicit refetch control. */
  refresh?: true
  sql: string
} & NormalizedFrameSqlOptions

/** Host -> frame: result for a previously issued guest-sql request. */
export type FrameQueryResultMessage = {
  type: 'guest-query-result'
  id: number
  /** Engine-ordered rows; readonly — reordering in the frame changes SQL semantics. */
  rows: readonly Record<string, unknown>[]
} & PaginatedFrameQueryResult

/** Host -> frame: failure for a previously issued guest-sql request. */
export type FrameQueryErrorMessage = {
  type: 'guest-query-error'
  id: number
  error: string
  /** Stable backend classification when the failure originated in GraphQL. */
  code?: string
  /** Server result cap associated with a result-too-large failure. */
  limitBytes?: number
  /** Bytes measured before the server rejected the result. */
  resultBytes?: number
  /** Oversized row-limit request parameter. */
  parameter?: UdsRowLimitParameter
  requestedRows?: number
  maxRows?: number
}

/** Frame -> host: call one endpoint as the viewer; `input` is the endpoint's input object. */
export type FrameEndpointCallMessage = {
  type: 'guest-endpoint-call'
  id: number
  logicalId: string
  input: Record<string, unknown>
}

/** Host -> frame: an endpoint's output, its dates tagged as the endpoints service sends them. */
export type FrameEndpointResultMessage = {
  type: 'guest-endpoint-result'
  id: number
  output: unknown
}

/** Host -> frame: a failed endpoint call, in the endpoints service's error vocabulary. */
export type FrameEndpointErrorMessage = {
  type: 'guest-endpoint-error'
  id: number
  error: string
  code?: string
  /** The HTTP status the service answered with; absent when the call never reached it. */
  status?: number
}

export { APP_GRAPHQL_DOCUMENTS, type AppGraphqlOperation }

/** Frame -> host: run one of the App row documents as the viewer. */
export type FrameGraphqlRequestMessage = {
  type: 'guest-graphql'
  id: number
  operation: AppGraphqlOperation
  variables: Record<string, unknown>
}

/** Host -> frame: the operation's `data`. */
export type FrameGraphqlResultMessage = {
  type: 'guest-graphql-result'
  id: number
  data: unknown
}

/** Host -> frame: the operation failed. */
export type FrameGraphqlErrorMessage = {
  type: 'guest-graphql-error'
  id: number
  error: string
}

/**
 * Frame -> host: the guest committed its first successful React render. This
 * closes the initial SQL batch; later requests are independent interactions.
 */
export type FrameRenderedMessage = { type: 'guest-frame-rendered' }

/** Frame -> host: the first render committed and every query it started has settled. */
export type FrameQueriesSettledMessage = { type: 'guest-frame-queries-settled' }

export const FRAME_RUNTIME_ERROR_PHASES = [
  'module_load',
  'render',
  'unhandled_error',
  'unhandled_rejection',
] as const
export type FrameRuntimeErrorPhase = (typeof FRAME_RUNTIME_ERROR_PHASES)[number]
export const MAX_GUEST_RUNTIME_ERROR_LENGTH = 10_000

/** Frame -> host: trusted runtime failure metadata, never guest source or compiled code. */
export type FrameRuntimeErrorMessage = {
  type: 'guest-frame-runtime-error'
  phase: FrameRuntimeErrorPhase
  error: string
}

/** Host -> frame: collect bounded runtime resource counters without exposing the port. */
export type FrameResourceSnapshotRequestMessage = {
  type: 'guest-resource-snapshot-request'
  id: number
  includeMemory: boolean
}

export const FRAME_MEMORY_SOURCES = [
  'user_agent_specific',
  'chromium_heap_estimate',
  'unsupported',
] as const
export type FrameMemorySource = (typeof FRAME_MEMORY_SOURCES)[number]

/** Frame -> host: one interval summary; long-task counters reset after each snapshot. */
export type FrameResourceSnapshotMessage = {
  type: 'guest-resource-snapshot'
  id: number
  domNodes: number
  longTaskCount: number
  longTaskDurationMs: number
  maxLongTaskDurationMs: number
  sampleWindowMs: number
  memorySource: FrameMemorySource
  memoryUsedBytes?: number
  memoryTotalBytes?: number
}

const FRAME_DIAGNOSTIC_LEVEL_BY_VALUE = { info: true, warning: true, error: true } as const
export type FrameDiagnosticLevel = keyof typeof FRAME_DIAGNOSTIC_LEVEL_BY_VALUE

/** Cap on a single relayed diagnostic message; the frame truncates before recording. */
export const MAX_FRAME_DIAGNOSTIC_LENGTH = 2_000

/** Bound on distinct retained diagnostics; the oldest is evicted first. */
export const MAX_FRAME_DIAGNOSTIC_ENTRIES = 100

/** One ledger row. `seq` is first-occurrence order; identical entries coalesce into `count`. */
export type FrameDiagnosticEntry = {
  seq: number
  level: FrameDiagnosticLevel
  message: string
  count: number
}

/**
 * Frame -> host: the whole ledger, reposted on every change — lifecycle, failed queries, captured
 * console output. The frame is opaque, so this is the host's only window into it.
 */
export type FrameDiagnosticsMessage = {
  type: 'guest-diagnostics'
  entries: readonly FrameDiagnosticEntry[]
}

/**
 * Cap on relayed annotation markdown. The frame truncates to this length before posting so a
 * legitimate oversized copy degrades to a truncated paste instead of silently vanishing at the
 * host guard.
 */
export const MAX_ANNOTATION_MARKDOWN_LENGTH = 200_000

/**
 * Frame -> host: annotation markdown from the in-jail Agentation toolbar's copy action. The
 * opaque-origin frame has no clipboard access, so the host writes it to the clipboard.
 */
export type FrameAnnotationCopyMessage = { type: 'guest-annotation-copy'; markdown: string }

/**
 * Frame -> host: a link leaving the guest document — absolute http(s) or a
 * protocol-handler scheme (mailto:, tel:) in v7; root-relative guest paths
 * join with intra-app routing. The host classifies the href: cross-origin
 * http(s) always opens a new tab; same-origin becomes an SPA navigation, or
 * a new tab when `newTab` is set (modified click, middle click,
 * target="_blank"); mailto:/tel: hand off to the OS protocol handler.
 */
export type FrameNavigationRequestMessage = {
  type: 'guest-navigation-request'
  href: string
  newTab?: boolean
}

/**
 * Frame -> host: open a destination named by logical id — a platform object or another app's
 * route. The host resolves it to its existing route and navigates there, in a new tab when
 * `newTab` is set; a missing or denied target is reported to the viewer.
 */
export type FrameDestinationRequestMessage = {
  type: 'guest-destination-request'
  destination: GuestDestination
  newTab?: boolean
}

/**
 * Frame -> host: resolve a destination to the host URL it opens, ahead of any click, so an
 * anchor's own href can carry it — copy link address and the status-bar preview then show the
 * real route. Answered by guest-destination-resolved with the same id.
 */
export type FrameDestinationResolveMessage = {
  type: 'guest-destination-resolve'
  id: number
  destination: GuestDestination
}

/** Host -> frame: the root-relative host URL a destination opens, or null when it opens nothing
 * for this viewer. */
export type FrameDestinationResolvedMessage = {
  type: 'guest-destination-resolved'
  id: number
  href: string | null
}

/** Frame -> host: a guest router intent; nothing renders until the guest-route-change echo. */
export type FrameNavigateMessage = {
  type: 'guest-navigate'
  path: string
  /** Omitted when empty. */
  search?: string
  replace?: boolean
}

/** Frame -> host: run history.go(delta) — real browser traversal, it may leave the embed. */
export type FrameHistoryGoMessage = {
  type: 'guest-history-go'
  delta: number
}

/** Cap on relayed guest titles; display copy only. */
export const MAX_GUEST_TITLE_LENGTH = 300

/**
 * Frame -> host: the frame document's title (React hoists guest <title> elements into it,
 * and document.title writes land there too). The mount appends it to the web title;
 * empty clears the suffix.
 */
export type FrameTitleMessage = {
  type: 'guest-title'
  title: string
}

/** History-entry ids are RR's 8-char keys (or 'default'); the cap only guards abuse. */
export const MAX_ROUTE_CHANGE_KEY_LENGTH = 64

/**
 * Host -> frame: the authoritative guest location, sent on every host URL change under the
 * mount — the only commit path in synced mode. `key` drives scroll restoration.
 */
export type FrameRouteChangeMessage = {
  type: 'guest-route-change'
  path: string
  search: string
  key: string
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isNonNegativeSafeInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0

const isNonNegativeFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0

/** Request ids share one id space per frame. */
export const isRequestId = isNonNegativeSafeInteger

// LEGACY_CJS_REMOVE: ENG-601 — Remove with FrameReadyMessage, not the shared RPC guards below.
export const isFrameReadyMessage = (value: unknown): value is FrameReadyMessage =>
  isRecord(value) &&
  value.type === 'guest-frame-ready' &&
  typeof value.protocolVersion === 'number' &&
  typeof value.buildSha === 'string' &&
  value.buildSha.length > 0 &&
  value.buildSha.length <= 64

export const isGuestRouteLocation = (value: unknown): value is GuestRouteLocation =>
  isRecord(value) &&
  typeof value.path === 'string' &&
  value.path.length > 0 &&
  value.path.length <= GUEST_NAVIGATION_BUDGETS.maxPathLength &&
  typeof value.search === 'string' &&
  value.search.length <= GUEST_NAVIGATION_BUDGETS.maxSearchLength

// LEGACY_CJS_REMOVE: ENG-601 — Remove with FrameInitMessage; route/query validation remains shared.
export const isFrameInitMessage = (value: unknown): value is FrameInitMessage =>
  isRecord(value) &&
  value.type === 'guest-init' &&
  typeof value.protocolVersion === 'number' &&
  typeof value.code === 'string' &&
  (value.annotationsEnabled === undefined || typeof value.annotationsEnabled === 'boolean') &&
  (value.route === undefined || isGuestRouteLocation(value.route))

export const isUdsSqlPage = (value: unknown): value is UdsSqlPage => {
  if (!isRecord(value) || !Number.isSafeInteger(value.limit) || (value.limit as number) <= 0) {
    return false
  }
  if (
    value.offset !== undefined &&
    (!Number.isSafeInteger(value.offset) || (value.offset as number) < 0)
  ) {
    return false
  }
  if (
    value.cursor !== undefined &&
    (typeof value.cursor !== 'string' ||
      value.cursor.length === 0 ||
      value.cursor.length > GUEST_QUERY_BUDGETS.maxSerializedQueryLength)
  ) {
    return false
  }
  return value.offset === undefined || value.cursor === undefined
}

export const isFrameSqlRequestMessage = (value: unknown): value is FrameSqlRequestMessage => {
  if (
    !isRecord(value) ||
    value.type !== 'guest-sql' ||
    !isRequestId(value.id) ||
    typeof value.sql !== 'string' ||
    (value.refresh !== undefined && value.refresh !== true)
  ) {
    return false
  }
  return (
    isUdsSqlPage(value.page) &&
    (value.includeTotalCount === undefined || typeof value.includeTotalCount === 'boolean')
  )
}

export const isFrameQueryResultMessage = (value: unknown): value is FrameQueryResultMessage => {
  if (
    !isRecord(value) ||
    value.type !== 'guest-query-result' ||
    !isRequestId(value.id) ||
    !Array.isArray(value.rows)
  ) {
    return false
  }
  if (typeof value.hasNext !== 'boolean' || value.truncated !== undefined) {
    return false
  }
  const offsetValid =
    value.offset === undefined ||
    (Number.isSafeInteger(value.offset) && (value.offset as number) >= 0)
  const cursorValid =
    value.cursor === undefined ||
    (typeof value.cursor === 'string' &&
      value.cursor.length > 0 &&
      value.cursor.length <= GUEST_QUERY_BUDGETS.maxSerializedQueryLength)
  const totalCountValid =
    value.totalCount === undefined ||
    (Number.isInteger(value.totalCount) &&
      (value.totalCount as number) >= 0 &&
      (value.totalCount as number) <= 2_147_483_647)
  const continuationCount = Number(value.offset !== undefined) + Number(value.cursor !== undefined)
  return offsetValid && cursorValid && totalCountValid && continuationCount <= Number(value.hasNext)
}

export const isFrameQueryResultForRequest = (
  value: unknown,
  request: FrameSqlOptions,
): value is FrameQueryResultMessage => {
  if (!isFrameQueryResultMessage(value)) {
    return false
  }
  if (request.page === undefined) {
    // Every request carries a page; a pageless shape cannot be matched.
    return false
  }
  if ((request.includeTotalCount === true) !== (value.totalCount !== undefined)) {
    return false
  }
  if (request.page.offset !== undefined) {
    return value.cursor === undefined
  }
  if (request.page.cursor !== undefined) {
    return value.offset === undefined
  }
  // The first mode-free page may choose either keyset or best-effort offset.
  return true
}

export const isFrameQueryErrorMessage = (value: unknown): value is FrameQueryErrorMessage =>
  isRecord(value) &&
  value.type === 'guest-query-error' &&
  isRequestId(value.id) &&
  typeof value.error === 'string' &&
  (value.code === undefined || typeof value.code === 'string') &&
  (value.limitBytes === undefined ||
    (typeof value.limitBytes === 'number' &&
      Number.isSafeInteger(value.limitBytes) &&
      value.limitBytes > 0)) &&
  (value.resultBytes === undefined ||
    (typeof value.resultBytes === 'number' &&
      Number.isSafeInteger(value.resultBytes) &&
      value.resultBytes >= 0)) &&
  (value.parameter === undefined ||
    value.parameter === 'maxRows' ||
    value.parameter === 'page.limit') &&
  (value.requestedRows === undefined ||
    (typeof value.requestedRows === 'number' &&
      Number.isSafeInteger(value.requestedRows) &&
      value.requestedRows > 0)) &&
  (value.maxRows === undefined ||
    (typeof value.maxRows === 'number' && Number.isSafeInteger(value.maxRows) && value.maxRows > 0))

export const isFrameEndpointCallMessage = (value: unknown): value is FrameEndpointCallMessage =>
  isRecord(value) &&
  value.type === 'guest-endpoint-call' &&
  isRequestId(value.id) &&
  typeof value.logicalId === 'string' &&
  value.logicalId.length > 0 &&
  value.logicalId.length <= GUEST_ENDPOINT_BUDGETS.maxLogicalIdLength &&
  isRecord(value.input) &&
  !Array.isArray(value.input)

export const isFrameEndpointResultMessage = (value: unknown): value is FrameEndpointResultMessage =>
  isRecord(value) &&
  value.type === 'guest-endpoint-result' &&
  isRequestId(value.id) &&
  'output' in value

export const isFrameEndpointErrorMessage = (value: unknown): value is FrameEndpointErrorMessage =>
  isRecord(value) &&
  value.type === 'guest-endpoint-error' &&
  isRequestId(value.id) &&
  typeof value.error === 'string' &&
  (value.code === undefined || typeof value.code === 'string') &&
  (value.status === undefined ||
    (Number.isSafeInteger(value.status) && (value.status as number) > 0))

export const isFrameGraphqlRequestMessage = (value: unknown): value is FrameGraphqlRequestMessage =>
  isRecord(value) &&
  value.type === 'guest-graphql' &&
  isRequestId(value.id) &&
  typeof value.operation === 'string' &&
  Object.hasOwn(APP_GRAPHQL_DOCUMENTS, value.operation) &&
  isRecord(value.variables) &&
  !Array.isArray(value.variables)

export const isFrameGraphqlResultMessage = (value: unknown): value is FrameGraphqlResultMessage =>
  isRecord(value) && value.type === 'guest-graphql-result' && isRequestId(value.id)

export const isFrameGraphqlErrorMessage = (value: unknown): value is FrameGraphqlErrorMessage =>
  isRecord(value) &&
  value.type === 'guest-graphql-error' &&
  isRequestId(value.id) &&
  typeof value.error === 'string'

export const isFrameRenderedMessage = (value: unknown): value is FrameRenderedMessage =>
  isRecord(value) && value.type === 'guest-frame-rendered'

export const isFrameQueriesSettledMessage = (value: unknown): value is FrameQueriesSettledMessage =>
  isRecord(value) && value.type === 'guest-frame-queries-settled'

const FRAME_RUNTIME_ERROR_PHASE_BY_VALUE: Record<FrameRuntimeErrorPhase, true> = {
  module_load: true,
  render: true,
  unhandled_error: true,
  unhandled_rejection: true,
}

const isFrameRuntimeErrorPhase = (value: unknown): value is FrameRuntimeErrorPhase =>
  typeof value === 'string' && Object.hasOwn(FRAME_RUNTIME_ERROR_PHASE_BY_VALUE, value)

export const isFrameRuntimeErrorMessage = (value: unknown): value is FrameRuntimeErrorMessage =>
  isRecord(value) &&
  value.type === 'guest-frame-runtime-error' &&
  isFrameRuntimeErrorPhase(value.phase) &&
  typeof value.error === 'string' &&
  value.error.length <= MAX_GUEST_RUNTIME_ERROR_LENGTH

const FRAME_MEMORY_SOURCE_BY_VALUE: Record<FrameMemorySource, true> = {
  user_agent_specific: true,
  chromium_heap_estimate: true,
  unsupported: true,
}

const isFrameMemorySource = (value: unknown): value is FrameMemorySource =>
  typeof value === 'string' && Object.hasOwn(FRAME_MEMORY_SOURCE_BY_VALUE, value)

export const isFrameResourceSnapshotRequestMessage = (
  value: unknown,
): value is FrameResourceSnapshotRequestMessage =>
  isRecord(value) &&
  value.type === 'guest-resource-snapshot-request' &&
  isRequestId(value.id) &&
  typeof value.includeMemory === 'boolean'

export const isFrameResourceSnapshotMessage = (
  value: unknown,
): value is FrameResourceSnapshotMessage =>
  isRecord(value) &&
  value.type === 'guest-resource-snapshot' &&
  isRequestId(value.id) &&
  Number.isSafeInteger(value.domNodes) &&
  isNonNegativeFiniteNumber(value.domNodes) &&
  Number.isSafeInteger(value.longTaskCount) &&
  isNonNegativeFiniteNumber(value.longTaskCount) &&
  isNonNegativeFiniteNumber(value.longTaskDurationMs) &&
  isNonNegativeFiniteNumber(value.maxLongTaskDurationMs) &&
  isNonNegativeFiniteNumber(value.sampleWindowMs) &&
  isFrameMemorySource(value.memorySource) &&
  (value.memoryUsedBytes === undefined || isNonNegativeFiniteNumber(value.memoryUsedBytes)) &&
  (value.memoryTotalBytes === undefined || isNonNegativeFiniteNumber(value.memoryTotalBytes))

const isFrameDiagnosticLevel = (value: unknown): value is FrameDiagnosticLevel =>
  typeof value === 'string' && Object.hasOwn(FRAME_DIAGNOSTIC_LEVEL_BY_VALUE, value)

const isFrameDiagnosticEntry = (value: unknown): value is FrameDiagnosticEntry =>
  isRecord(value) &&
  isNonNegativeSafeInteger(value.seq) &&
  isFrameDiagnosticLevel(value.level) &&
  typeof value.message === 'string' &&
  value.message.length <= MAX_FRAME_DIAGNOSTIC_LENGTH &&
  isNonNegativeSafeInteger(value.count)

export const isFrameDiagnosticsMessage = (value: unknown): value is FrameDiagnosticsMessage =>
  isRecord(value) &&
  value.type === 'guest-diagnostics' &&
  Array.isArray(value.entries) &&
  value.entries.length <= MAX_FRAME_DIAGNOSTIC_ENTRIES &&
  value.entries.every(isFrameDiagnosticEntry)

export const isFrameAnnotationCopyMessage = (value: unknown): value is FrameAnnotationCopyMessage =>
  isRecord(value) &&
  value.type === 'guest-annotation-copy' &&
  typeof value.markdown === 'string' &&
  value.markdown.length > 0 &&
  value.markdown.length <= MAX_ANNOTATION_MARKDOWN_LENGTH

export const isFrameDestinationRequestMessage = (
  value: unknown,
): value is FrameDestinationRequestMessage =>
  isRecord(value) &&
  value.type === 'guest-destination-request' &&
  isGuestDestination(value.destination) &&
  (value.newTab === undefined || typeof value.newTab === 'boolean')

export const isFrameDestinationResolveMessage = (
  value: unknown,
): value is FrameDestinationResolveMessage =>
  isRecord(value) &&
  value.type === 'guest-destination-resolve' &&
  isRequestId(value.id) &&
  isGuestDestination(value.destination)

/** A resolved href is a well-formed root-relative host route, or null. */
export const isFrameDestinationResolvedMessage = (
  value: unknown,
): value is FrameDestinationResolvedMessage =>
  isRecord(value) &&
  value.type === 'guest-destination-resolved' &&
  isRequestId(value.id) &&
  (value.href === null ||
    (typeof value.href === 'string' && parseGuestHref(value.href) !== undefined))

export const isFrameNavigationRequestMessage = (
  value: unknown,
): value is FrameNavigationRequestMessage =>
  isRecord(value) &&
  value.type === 'guest-navigation-request' &&
  typeof value.href === 'string' &&
  value.href.length > 0 &&
  value.href.length <= GUEST_NAVIGATION_BUDGETS.maxHrefLength &&
  (value.newTab === undefined || typeof value.newTab === 'boolean')

export const isFrameNavigateMessage = (value: unknown): value is FrameNavigateMessage =>
  isRecord(value) &&
  value.type === 'guest-navigate' &&
  typeof value.path === 'string' &&
  value.path.length > 0 &&
  value.path.length <= GUEST_NAVIGATION_BUDGETS.maxPathLength &&
  (value.search === undefined ||
    (typeof value.search === 'string' &&
      value.search.length <= GUEST_NAVIGATION_BUDGETS.maxSearchLength)) &&
  (value.replace === undefined || typeof value.replace === 'boolean')

export const isFrameHistoryGoMessage = (value: unknown): value is FrameHistoryGoMessage =>
  isRecord(value) &&
  value.type === 'guest-history-go' &&
  typeof value.delta === 'number' &&
  Number.isSafeInteger(value.delta) &&
  value.delta !== 0 &&
  Math.abs(value.delta) <= GUEST_NAVIGATION_BUDGETS.maxHistoryTraversalDelta

// oxlint-disable-next-line no-control-regex -- control characters never belong in a title
const TITLE_DENIED_CHARS = /[\u0000-\u001F\u007F]/

export const isFrameTitleMessage = (value: unknown): value is FrameTitleMessage =>
  isRecord(value) &&
  value.type === 'guest-title' &&
  typeof value.title === 'string' &&
  value.title.length <= MAX_GUEST_TITLE_LENGTH &&
  !TITLE_DENIED_CHARS.test(value.title)

export const isFrameRouteChangeMessage = (value: unknown): value is FrameRouteChangeMessage =>
  isRecord(value) &&
  value.type === 'guest-route-change' &&
  typeof value.path === 'string' &&
  value.path.length > 0 &&
  value.path.length <= GUEST_NAVIGATION_BUDGETS.maxPathLength &&
  typeof value.search === 'string' &&
  value.search.length <= GUEST_NAVIGATION_BUDGETS.maxSearchLength &&
  typeof value.key === 'string' &&
  value.key.length > 0 &&
  value.key.length <= MAX_ROUTE_CHANGE_KEY_LENGTH

// ---------------------------------------------------------------------------
// Guest-space route sanitizers, shared by both ends of the wire (the frame checks before
// posting; the host re-checks, never trusting the frame). Reject, never repair: a value
// that fails any rule is dropped whole and surfaces as a frame diagnostic.
// ---------------------------------------------------------------------------

// oxlint-disable-next-line no-control-regex -- rejecting raw control characters is the point
const PATH_DENIED_CHARS = /[?#\\\s\u0000-\u001F\u007F]/
// oxlint-disable-next-line no-control-regex
const SEARCH_DENIED_CHARS = /[#\\\u0000-\u001F\u007F]/

const decodes = (value: string): boolean => {
  try {
    decodeURIComponent(value)
    return true
  } catch {
    return false
  }
}

/** Root-relative, canonical segments (no empty/'.'/'..', no trailing slash), valid encoding. */
export const sanitizeGuestPath = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || value.length === 0) {
    return undefined
  }
  if (value.length > GUEST_NAVIGATION_BUDGETS.maxPathLength) {
    return undefined
  }
  if (!value.startsWith('/') || value.startsWith('//')) {
    return undefined
  }
  if (PATH_DENIED_CHARS.test(value) || !decodes(value)) {
    return undefined
  }
  if (value !== '/') {
    const segments = value.slice(1).split('/')
    if (segments.some((segment) => segment === '' || segment === '.' || segment === '..')) {
      return undefined
    }
  }
  return value
}

/** '' or one well-formed non-empty '?...' with valid encoding. */
export const sanitizeGuestSearch = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined
  }
  if (value === '') {
    return ''
  }
  if (!value.startsWith('?') || value.length < 2) {
    return undefined
  }
  if (value.length > GUEST_NAVIGATION_BUDGETS.maxSearchLength) {
    return undefined
  }
  if (SEARCH_DENIED_CHARS.test(value) || !decodes(value)) {
    return undefined
  }
  return value
}

/** Split a root-relative href into a sanitized route; fragments are rejected whole. */
export const parseGuestHref = (href: string): GuestRouteLocation | undefined => {
  if (href.includes('#')) {
    return undefined
  }
  const queryStart = href.indexOf('?')
  const path = sanitizeGuestPath(queryStart === -1 ? href : href.slice(0, queryStart))
  const search = sanitizeGuestSearch(queryStart === -1 ? '' : href.slice(queryStart))
  if (path === undefined || search === undefined) {
    return undefined
  }
  return { path, search }
}

const DESTINATION_LOGICAL_ID = /^[A-Za-z0-9_-]{1,256}$/

const isDestinationKind = (value: unknown): value is GuestDestinationKind =>
  GUEST_DESTINATION_KINDS.some((kind) => kind === value)

/** A well-formed destination: a known kind, a logical id, and a path/search only on `app`. */
export const isGuestDestination = (value: unknown): value is GuestDestination => {
  if (!isRecord(value) || !isDestinationKind(value.kind)) {
    return false
  }
  if (typeof value.logicalId !== 'string' || !DESTINATION_LOGICAL_ID.test(value.logicalId)) {
    return false
  }
  if (value.kind !== 'app') {
    return value.path === undefined && value.search === undefined
  }
  return (
    (value.path === undefined || sanitizeGuestPath(value.path) === value.path) &&
    (value.search === undefined || sanitizeGuestSearch(value.search) === value.search)
  )
}

/** The destination a reserved href names, or undefined — reject, never repair. */
export const parseDestinationHref = (href: string): GuestDestination | undefined => {
  if (!href.startsWith(GUEST_DESTINATION_PREFIX)) {
    return undefined
  }
  const route = parseGuestHref(href)
  if (route === undefined) {
    return undefined
  }
  const [kind, logicalId, ...rest] = route.path.slice(GUEST_DESTINATION_PREFIX.length).split('/')
  const path = rest.length === 0 ? undefined : `/${rest.join('/')}`
  const search = route.search === '' ? undefined : route.search
  const destination = {
    kind,
    logicalId,
    ...(path === undefined ? {} : { path }),
    ...(search === undefined ? {} : { search }),
  }
  return isGuestDestination(destination) ? destination : undefined
}

// LEGACY_CJS_REMOVE: ENG-601 — Retire after static-frame loading and HTML resource accounting migrate.
/** Document served to the sandboxed iframe; bundled as a classic IIFE asset. */
export const GUEST_FRAME_PATH = '/guest-frame.html'
